import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import { round } from 'lodash';
import CardBox from '../../CardBox';
import { useTranslation } from '../../../src/i18n';
import DeleteFbIcon from '../../Icons/DeleteFbIcon';
import { TP } from '../../../constants/consts';
import TextButton from '../../TextButton';
import { colors } from '../../../constants/styleConstants';
import OuterLink from '../../OuterLink';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  fileName: {
    margin: 0,
    marginLeft: theme.spacing(2),
    color: colors.textColor,
  },
  name: {
    fontWeight: 800,
  },
  size: {
    color: colors.gray,
  },
}));

const MIN_BYTES_FILE = 11;
const KB_FILE = 1024;
const MB_FILE = 1048576; // 1024 * 1024

const EditFileCard = ({ className, onDelete, file }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getSize = React.useCallback(() => {
    if (file?.size < MIN_BYTES_FILE) {
      return `${file?.size} bytes`;
    }
    if (file?.size >= MB_FILE) {
      return `${round(file?.size / MB_FILE, 2)} mb`;
    }
    return file?.size ? `${round(file?.size / KB_FILE, 2)} kb` : '';
  }, [file]);

  return (
    <CardBox className={cn(classes.root, className)}>
      <div className={classes.fileInfo}>
        {file?.type?.includes('pdf') ? <PictureAsPdfIcon /> : <DescriptionIcon />}
        <div>
          {file?.url ? (
            <OuterLink href={file?.url} className={cn(classes.fileName, classes.name)}>
              {file?.name || file.url}
            </OuterLink>
          ) : (
            <p className={cn(classes.fileName, classes.name)}>{file?.name}</p>
          )}
          <p className={cn(classes.fileName, classes.size)}>{getSize()}</p>
        </div>
      </div>
      {onDelete && (
        <TextButton startIcon={<DeleteFbIcon />} onClick={onDelete}>
          {t(`${TP}.m_DELETE`)}
        </TextButton>
      )}
    </CardBox>
  );
};

EditFileCard.propTypes = {
  className: PropTypes.string,
  onDelete: PropTypes.func,
  file: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
};

export default memo(EditFileCard);
